import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PresetCarousel from '../components/video-generate/PresetCarousel';
import ReferenceUploadFlow from '../components/catalog/ReferenceUploadFlow';
import CreateActionControls from '../components/create/CreateActionControls';
import { useCatalog } from '../hooks/useCatalog';
import { useTelegram } from '../hooks/useTelegram';
import useVideoGeneration from '../hooks/useVideoGeneration';
import { session } from '../platform';
import { trackClientEvent } from '../analytics/track';
import { useAuthStore } from '../store/authStore';
import useConsentGuard from '../hooks/useConsentGuard';
import { getVideoCreditCost } from '../core/generationPricing';
import { getPhotoCount } from '../utils/photoReferences';

const DEFAULT_ASPECT = '9:16';
const DEFAULT_QUALITY = 'LQ';

function normalizeVideoQuality(value) {
  return value === 'LQ' || value === 'Q_480P' || value === '480p' ? 'LQ' : 'MQ';
}

function resolvePreset(sections, presetId) {
  if (!sections || !presetId) return null;
  for (const group of sections) {
    const found = (group.presets || []).find(
      (p) => String(p.id) === String(presetId)
    );
    if (found) return found;
  }
  return null;
}

function flattenPresets(sections) {
  if (!Array.isArray(sections)) return [];
  const out = [];
  for (const group of sections) {
    if (!Array.isArray(group?.presets)) continue;
    for (const preset of group.presets) {
      if (preset?.id) out.push(preset);
    }
  }
  return out;
}

function findPresetIndexById(flatPresets, presetId) {
  if (!presetId) return -1;
  return flatPresets.findIndex((p) => String(p.id) === String(presetId));
}

function isVideoUrl(url = '') {
  return /\.(mp4|webm|mov)(\?.*)?$/i.test(String(url));
}

function pluralCreditsKey(count) {
  const mod100 = Math.abs(count) % 100;
  const mod10 = Math.abs(count) % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'credits5';
  if (mod10 === 1) return 'credit';
  if (mod10 >= 2 && mod10 <= 4) return 'credits234';
  return 'credits5';
}

function PresetMedia({ preset }) {
  const previewUrl = preset?.previewClipUrl;
  const mediaUrl = isVideoUrl(previewUrl) ? previewUrl : preset?.coverUrl;
  if (!mediaUrl) {
    return <div className="h-full w-full skeleton-shimmer" style={{ '--skeleton-base': '#151517' }} />;
  }

  if (isVideoUrl(mediaUrl)) {
    return (
      <video
        src={mediaUrl}
        className="h-full w-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        poster={preset?.coverUrl || undefined}
        data-testid="preset-cover-bg"
      />
    );
  }

  return (
    <img
      src={mediaUrl}
      alt=""
      className="h-full w-full object-cover"
      loading="eager"
      decoding="async"
      data-testid="preset-cover-bg"
    />
  );
}

export default function VideoGenerate() {
  const { t } = useTranslation();
  const { presetId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useTelegram();
  const { requireConsent } = useConsentGuard();
  const credits = useAuthStore((state) => state.credits);

  const { items: sections, loading: catalogLoading } = useCatalog('video', 'all-video');
  const { generate, loading: generating, error: genError } = useVideoGeneration();

  const preset = resolvePreset(sections, presetId);
  const flatPresets = flattenPresets(sections);
  const currentIndex = findPresetIndexById(flatPresets, presetId);
  const presetGroup = (sections || []).find((group) =>
    (group.presets || []).some((item) => String(item.id) === String(presetId))
  );
  const titleImage = preset?.titleImage || preset?.logoUrl || null;
  const requiredPhotoCount = getPhotoCount(preset);

  const [aspectRatio] = useState(() => {
    const v = session.getItem('repeatVideoAspectRatio');
    if (v) {
      session.removeItem('repeatVideoAspectRatio');
      return v;
    }
    return DEFAULT_ASPECT;
  });
  const [quality, setQuality] = useState(() => {
    const v = session.getItem('repeatVideoQuality');
    if (v) {
      session.removeItem('repeatVideoQuality');
      return normalizeVideoQuality(v);
    }
    return DEFAULT_QUALITY;
  });
  const [references, setReferences] = useState(() => {
    const v = session.getItem('repeatVideoReference');
    if (v) {
      session.removeItem('repeatVideoReference');
      return [{
        id: 'repeat-reference',
        preview: v,
        url: v,
        source: 'gallery',
        owned: false,
      }];
    }
    return [];
  });
  const [uploadingReference, setUploadingReference] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    if (!preset) return;
    trackClientEvent('preset_opened', {
      presetId: preset.id,
      groupId: presetGroup?.id || null,
    });
  }, [preset?.id, presetGroup?.id]);

  const handlePresetSwipe = useCallback(
    (newIndex) => {
      const next = flatPresets[newIndex];
      if (!next?.id) return;
      navigate(`/video-generate/${next.id}`, { replace: true });
    },
    [flatPresets, navigate]
  );

  const performGenerate = async () => {
    const referenceImageUrls = references
      .slice(0, requiredPhotoCount)
      .map((reference) => reference?.url)
      .filter(Boolean);
    if (referenceImageUrls.length !== requiredPhotoCount) return;

    await generate({
      presetId: preset.id,
      aspectRatio,
      quality,
      referenceImageUrl: referenceImageUrls[0],
      referenceImageUrls,
    });
  };

  const handleGenerate = () => {
    if (!preset) return;
    if (references.slice(0, requiredPhotoCount).some((reference) => !reference?.url)
      || references.length < requiredPhotoCount) {
      showAlert(t('videoGenerate.pickPhotoRequired', 'Please upload a photo first.'));
      return;
    }

    requireConsent(performGenerate, 'generate');
  };

  if (!catalogLoading && !preset) {
    return (
      <div
        className="min-h-screen bg-bg-app flex flex-col items-center justify-center gap-4"
        data-testid="video-generate-not-found"
      >
        <span className="text-text-secondary font-sans text-chip-label">
          {t('videoGenerate.notFound')}
        </span>
        <button
          type="button"
          className="font-sans text-chip-label text-coin-yellow underline"
          onClick={() => navigate('/video-catalog')}
        >
          {t('videoGenerate.backToCatalog')}
        </button>
      </div>
    );
  }

  const ctaCost = getVideoCreditCost(quality);
  const busy = generating || uploadingReference;
  const isPair = requiredPhotoCount > 1;
  const pickTitle = isPair
    ? t('photoCatalog.pickTwoSelfies', 'Pick 2 Selfies')
    : t('videoGenerate.pickPhoto', 'Pick A Photo');
  const pickHint = isPair
    ? t(
        'photoCatalog.pickTwoHint',
        'Please upload two high quality photos where a face is clearly visible.'
      )
    : preset?.inputType === 'pet'
      ? t('videoGenerate.pickPetHint', 'Upload a high quality photo of your pet')
      : t(
          'videoGenerate.pickPhotoHint',
          'Upload a high quality photo for better video results'
        );
  const referencesReady = Array.from({ length: requiredPhotoCount })
    .every((_, index) => Boolean(references[index]?.url));
  const balanceValue = Number(credits?.total ?? 0) || 0;
  const totalCreditsWord = t(`prompt.${pluralCreditsKey(ctaCost)}`);

  return (
    <div
      className="relative h-[100dvh] min-h-screen w-full bg-bg-app overflow-hidden flex flex-col"
      data-testid="video-generate-page"
    >
      <PresetCarousel
        presets={flatPresets}
        currentIndex={currentIndex}
        onSwipe={handlePresetSwipe}
      >
        <div className="absolute inset-0" aria-hidden>
          <PresetMedia preset={preset} />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.16) 0%, rgba(0,0,0,0) 42%, rgba(1,1,3,0.78) 72%, #151518 100%)',
            }}
          />
          {titleImage && (
            <img
              src={titleImage}
              alt=""
              className="absolute bottom-4 left-1/2 z-10 h-auto max-h-[56px] max-w-[37%] -translate-x-1/2 object-contain pointer-events-none"
              style={{ width: '37%' }}
              loading="lazy"
              draggable={false}
              data-testid="preset-title-image"
            />
          )}
        </div>
      </PresetCarousel>

      <div
        className="relative z-20 shrink-0 rounded-t-3xl bg-popup-gradient px-6 pt-6"
        style={{ paddingBottom: 'calc(var(--safe-bottom, 0px) + 24px)' }}
        data-testid="bottom-panel"
        data-sheet-mode="static"
      >
        <ReferenceUploadFlow
          title={pickTitle}
          hint={pickHint}
          references={references}
          requiredCount={requiredPhotoCount}
          disabled={generating}
          onReferencesChange={setReferences}
          onUploadingChange={setUploadingReference}
          onError={setUploadError}
          cameraLabel={t('videoGenerate.camera', 'Camera')}
          galleryLabel={t('videoGenerate.gallery', 'Gallery')}
          removeLabel={t('common.delete')}
          testIdPrefix="video"
          galleryInputTestId="video-gallery-input"
          pairTestId="video-reference-pair"
        />

        <div
          className={[
            'overflow-hidden transition-all duration-200 ease-out',
            referencesReady
              ? 'mb-3 max-h-16 translate-y-0 opacity-100'
              : 'max-h-0 translate-y-3 opacity-0',
          ].join(' ')}
          aria-hidden={!referencesReady}
          data-testid="video-progressive-controls"
        >
          <button
            type="button"
            role="switch"
            aria-checked={quality === 'MQ'}
            aria-label={t('videoGenerate.highQuality', 'High quality')}
            onClick={() => setQuality((current) => (current === 'MQ' ? 'LQ' : 'MQ'))}
            className="flex w-full items-center justify-between rounded-2xl border border-white/[0.08] bg-white/[0.06] px-4 py-3 text-left transition-colors duration-200"
            data-testid="high-quality-toggle"
          >
            <span className="font-sans text-[15px] font-semibold leading-none text-text-primary">
              {t('videoGenerate.highQuality', 'High quality')}
            </span>
            <span
              className={[
                'relative h-7 w-12 rounded-full transition-colors duration-200',
                quality === 'MQ' ? 'bg-[#FFAC0B]' : 'bg-white/[0.14]',
              ].join(' ')}
              aria-hidden="true"
            >
              <span
                className={[
                  'absolute top-1 h-5 w-5 rounded-full bg-white transition-transform duration-200',
                  quality === 'MQ' ? 'translate-x-6' : 'translate-x-1',
                ].join(' ')}
              />
            </span>
          </button>
        </div>

        <CreateActionControls
          batchCount={1}
          totalCredits={ctaCost}
          totalCreditsWord={totalCreditsWord}
          balanceValue={balanceValue}
          createLabel={t('prompt.create', 'Create')}
          balanceLabel={t('prompt.balance', 'Balance')}
          generatingLabel={t('videoGenerate.creating', 'Creating...')}
          loading={busy}
          disabled={!referencesReady}
          disabledVariant="gold"
          showCounter={false}
          onCreate={handleGenerate}
          onDecrease={() => {}}
          onIncrease={() => {}}
          buttonTestId="cta-generate-btn"
        />

        {(genError || uploadError) && (
          <p
            className="mt-3 text-center font-sans text-chip-label text-red-500"
            data-testid="gen-error"
          >
            {t('videoGenerate.errorRetry')}
          </p>
        )}
      </div>

    </div>
  );
}
